import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button, Empty,
} from 'antd';
import {
  TitleWithActions, CustomPagination, FiltersWithIcons, Input,
} from 'components/components';
import GridCard from 'components/components/Card/View';
import DrawerFrame from 'components/components/Drawer/Frame';
import { categoriesIconsVol2 } from 'components/ServicesAndServiceProviders/utils';
import { servicesColumns } from 'components/utils/constants/columns';
import { ServiceType, convertGetServiceResponse } from 'packages/service_repository';
import ROLES from 'utils/constants/roles';
import { SearchIcon } from 'assets/icons';
import {
  useAddPortalOrder, usePortalPortfolio, usePortalScope, usePortalService, usePortalServices,
} from 'packages/venture_builder_repository/portal/hooks';
import { PortalScope } from 'packages/venture_builder_repository/portal/types';
import { selectedStartup, UncertainOrderError } from 'packages/venture_builder_repository/portal/utils';
import showNotification from 'services/utils/showNotification';
import routes from 'routes';
import OrderForm from './OrderForm';
import {
  EmptyPortfolio, LoadError, Loading, Refresh,
} from './shared';
import './VentureBuilderPortal.scss';

const OrderDrawer = ({ scope, id, onClose }: { scope: PortalScope, id: number, onClose: () => void }) => {
  const history = useHistory();
  const location = useLocation();
  const portfolio = usePortalPortfolio(scope);
  const service = usePortalService(scope, id);
  const mutation = useAddPortalOrder(scope);
  const error = portfolio.error || service.error;
  const startup = selectedStartup(portfolio.data || [], new URLSearchParams(location.search).get('startup') || undefined);
  return (
    <DrawerFrame
      className="drawer--blue"
      title={service.data?.name || 'Request service'}
      open
      destroyOnClose
      onClose={() => { if (!mutation.isPending) onClose(); }}
      closable={!mutation.isPending}
      maskClosable={!mutation.isPending}
      keyboard={!mutation.isPending}
    >
      {(portfolio.isPending || service.isPending) && <Loading />}
      {error && <LoadError error={error} retry={() => { portfolio.refetch(); service.refetch(); }} />}
      {mutation.error && (
        <div role="alert">
          {mutation.error.message}
          . Check Requests before retrying.
          <Button onClick={() => history.push(routes.requestsUrl)}>Review requests</Button>
        </div>
      )}
      {!error && portfolio.data && service.data && (
        portfolio.data.length ? (
          <OrderForm
            service={service.data}
            startups={portfolio.data}
            initialStartupId={startup?.id}
            pending={mutation.isPending}
            blocked={mutation.error instanceof UncertainOrderError}
            onCancel={onClose}
            onSubmit={(order) => mutation.mutate(order, {
              onSuccess: () => {
                showNotification('Service added to requests');
                history.push(routes.requestsUrl);
              },
            })}
          />
        ) : <EmptyPortfolio />
      )}
    </DrawerFrame>
  );
};

const VentureBuilderServices = () => {
  const scope = usePortalScope();
  const services = usePortalServices(scope);
  const [search, setSearch] = useState('');
  const [serviceId, setServiceId] = useState<number>();
  const [category, setCategory] = useState<number | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const catalogue = services.data || [];
  const categories = Array.from(new Map(catalogue.filter((service) => service.category?.id)
    .map((service) => [service.category!.id, service.category!])).values());
  const filtered = catalogue.filter((service) => (
    (category === 'all' || (service.categoryID || service.category?.id) === category)
    && `${service.name} ${service.serviceProvider.name} ${service.description || ''}`
      .toLowerCase().includes(search.trim().toLowerCase())
  ));
  const page = Math.max(1, Math.min(currentPage, Math.ceil(filtered.length / limit)));
  const cards = filtered.slice((page - 1) * limit, page * limit).map((service) => ({
    ...convertGetServiceResponse({ ...service, category: service.category || { id: service.categoryID || 0 } }),
    // An absent catalogue price is not a free service.
    ...(!service.price ? { priceFrom: '', priceTo: '' } : {}),
  }));
  return (
    <div className="content-container vb-portal">
      <TitleWithActions title="Services" subtitle="Browse services for your startups." size="l">
        <Refresh onClick={() => { services.refetch(); }} loading={services.isFetching} />
      </TitleWithActions>
      <Input className="vb-portal__search" layout="vertical">
        <Input.InputField
          prefix={<SearchIcon />}
          bordered={false}
          placeholder="Search services"
          aria-label="Search services"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setCurrentPage(1); }}
          allowClear
        />
      </Input>
      <FiltersWithIcons
        categories={categories}
        icons={categoriesIconsVol2}
        activeFilters={category === 'all' ? [] : [category]}
        setFilter={({ data }: { data: number | 'all' }) => { setCategory(data === category ? 'all' : data); setCurrentPage(1); }}
      />
      {services.isPending && <Loading />}
      {services.error && <LoadError error={services.error} retry={() => { services.refetch(); }} />}
      {!services.error && !services.isPending && (
        <>
          {filtered.length ? (
            <GridCard
              component="startup-services"
              userType={ROLES.ventureBuilder}
              startupStatus=""
              angelInvestorStatus=""
              columns={servicesColumns}
              dataSource={cards}
              onGridCardClick={() => {}}
              onDeleteService={() => {}}
              renderServiceActions={(service: ServiceType) => (
                <Button className="primary-button md" onClick={() => setServiceId(service.id)}>Request service</Button>
              )}
            />
          ) : <Empty description={search || category !== 'all' ? 'No matching services' : 'No services available'} />}
          {!!filtered.length && (
            <CustomPagination
              gridLayout
              currentPage={page}
              limit={limit}
              total={filtered.length}
              setCurrentPage={setCurrentPage}
              setLimit={(value) => { setLimit(value); setCurrentPage(1); }}
            />
          )}
        </>
      )}
      {serviceId && <OrderDrawer scope={scope} id={serviceId} onClose={() => setServiceId(undefined)} />}
    </div>
  );
};

export default VentureBuilderServices;
